import React, { useContext, useState, useCallback } from "react";
import { Grid, Typography, Button } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useDropzone } from "react-dropzone";

// Contexts
import FacebookAdContext from "../../../../context/facebook_profiles/ad/AdContext";
import LayoutContext from "../../../../context/layout/LayoutContext";

// Components
import ImageSelection from "./ImageSelection";

const useStyles = makeStyles((theme) => ({
  root: {
    marginBottom: "0.5rem",
  },
  dropzone: {
    height: 120,
    backgroundColor: "#fafafa",
    borderStyle: "solid",
    borderStyle: "inset",
    borderWidth: 1,
    "&:focus": {
      outline: "none",
    },
    justifyContent: "center",
    alignItems: "center",
  },
  dropzoneError: {
    height: 120,
    backgroundColor: "#fafafa",
    borderStyle: "solid",
    borderWidth: 2,
    borderColor: theme.palette.error.main,
    "&:focus": {
      outline: "none",
    },
    justifyContent: "center",
    alignItems: "center",
  },
  helperText1: {
    textAlign: "center",
    color: "red",
  },
  helperText2: {
    textAlign: "center",
  },
  image: {
    "&:focus": {
      outline: "none",
    },
    objectFit: "contain",
  },
  button: {
    textTransform: "none",
    "&:focus": {
      outline: "none",
    },
  },
}));

const Image = ({ ad_id, ad_details, ad_errors }) => {
  const classes = useStyles();

  const {
    uploadImage,
    setImageUrl,
    setFormErrors,
    form_errors,
    setFormDetails,
    form_details,
  } = useContext(FacebookAdContext);
  const { setAlert, clearAlerts } = useContext(LayoutContext);

  const fileInputState = ad_details.image_state.fileInputState;
  const previewSource = ad_details.image_state.previewSource;
  const selectedFile = ad_details.image_state.selectedFile;

  const [browserOpen, setBrowserOpen] = useState(false);

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];

    if (file) {
      let filename = file.name;

      // if (file.name.includes(".png")) {
      //   filename = file.name.replace(".png", "");
      // }
      // if (file.name.includes(".jpg")) {
      //   filename = file.name.replace(".jpg", "");
      // }
      // if (file.name.includes(".jpeg")) {
      //   filename = file.name.replace(".jpeg", "");
      // }

      clearAlerts();
      previewFile(file);

      // Set Image State - (fileInputState, selectedFile)
      form_details[ad_id] = {
        ...form_details[ad_id],
        image_state: {
          ...form_details[ad_id]["image_state"],
          fileInputState: filename,
          selectedFile: file,
        },
      };
      setFormDetails(form_details);

      // Reset Image Url
      setImageUrl("", ad_id);

      // Set Image Error
      form_errors[ad_id] = {
        ...form_errors[ad_id],
        image: "Image is not selected or uploaded!",
      };
      setFormErrors(form_errors);
    }
  }, []);

  const onDropRejected = useCallback(() => {
    setAlert("Image file only.", "error");
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    accept: "image/*",
    multiple: false,
    onDrop,
    onDropRejected,
  });

  const previewFile = (file) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = () => {
      // Set Image State - (previewSource)
      form_details[ad_id] = {
        ...form_details[ad_id],
        image_state: {
          ...form_details[ad_id]["image_state"],
          previewSource: reader.result,
        },
      };

      setFormDetails(form_details);
    };
  };

  const onUpload = (e) => {
    e.preventDefault();

    if (!selectedFile) return;

    const reader = new FileReader();
    reader.readAsDataURL(selectedFile);
    reader.onloadend = () => uploadImage(reader.result, fileInputState, ad_id);
  };

  const onBrowse = () => {
    setBrowserOpen(true);
  };

  const browserClose = () => {
    // No Image Selected
    setBrowserOpen(false);
  };

  const browserSave = (image_url, image_filename) => {
    // Video Selected
    // Set Image State - (previewSource, fileInputState)
    form_details[ad_id] = {
      ...form_details[ad_id],
      image_state: {
        fileInputState: image_filename,
        previewSource: image_url,
        selectedFile: selectedFile,
      },
    };
    setFormDetails(form_details);

    // Set Image Url
    setImageUrl(image_url, ad_id);

    // Reset Image Error
    form_errors[ad_id] = { ...ad_errors, image: "" };
    setFormErrors(form_errors);
    browserClose();
  };

  return (
    <Grid item xs={12} container className={classes.root}>
      <ImageSelection
        browserOpen={browserOpen}
        browserClose={browserClose}
        browserSave={browserSave}
        image_url={ad_details.image}
      />
      <Grid item xs={3}>
        <Grid
          item
          container
          className={ad_errors.image ? classes.dropzoneError : classes.dropzone}
          {...getRootProps()}
        >
          {previewSource ? (
            <img
              height="114"
              width="212"
              src={previewSource}
              className={classes.image}
            />
          ) : (
            <Grid item className={classes.helperText2}>
              <input {...getInputProps()} />{" "}
              <Typography variant="body1"> IMAGE </Typography>{" "}
              <Typography variant="overline">
                Drag and drop file here <br />
                or click to select file{" "}
              </Typography>{" "}
            </Grid>
          )}{" "}
        </Grid>{" "}
      </Grid>
      <Grid item xs={5} container justifyContent="space-evenly" alignItems="center">
        <Grid item xs={11}>
          <Typography variant="body2" className={classes.helperText1}>
            {" "}
            {ad_errors.image ? (
              ad_errors.image
            ) : previewSource ? (
              "Drag and drop file into the box to replace the image."
            ) : (
              <span> & emsp; </span>
            )}{" "}
          </Typography>
          <Typography variant="body1" className={classes.helperText2}>
            {" "}
            {fileInputState ? (
              <span>
                File name: <strong> {fileInputState} </strong>{" "}
              </span>
            ) : (
              <span> & emsp; </span>
            )}{" "}
          </Typography>{" "}
        </Grid>
        <Grid item xs={4}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            className={classes.button}
            onClick={onBrowse}
          >
            Browse{" "}
          </Button>{" "}
        </Grid>
        <Grid item xs={4}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            className={classes.button}
            onClick={onUpload}
            disabled={previewSource ? (ad_details.image ? true : false) : true}
          >
            Upload{" "}
          </Button>{" "}
        </Grid>{" "}
      </Grid>{" "}
    </Grid>
  );
};

export default Image;
